import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Tooltip,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormControl,
  FormLabel,
  InputAdornment,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Divider,
  Switch,
  Select,
  MenuItem,
  OutlinedInput,
  ListItemText,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  ContentCopy as ContentCopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  createToken,
  fetchTokens,
  revokeToken,
  deleteToken,
  clearTokenCreationResult,
} from "../../redux/tokenSlice";
import { PersonalAccessToken } from "../../models/PersonalAccessToken";
import { TokenScope } from "../../models/TokenScope";
import { CreateTokenRequest } from "../../models/CreateTokenRequest";

const PersonalAccessTokenManagement: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { tokens, loading, error, createdToken } = useAppSelector(
    (state) => state.tokens,
  );

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTokenId, setSelectedTokenId] = useState<number | null>(null);
  const [showToken, setShowToken] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  // Form state
  const [formData, setFormData] = useState<CreateTokenRequest>({
    name: "",
    scopes: [],
    expirationDays: 30,
    description: "",
  });

  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [expandedScope, setExpandedScope] = useState<string | null>(null);

  // Available scopes (would come from API)
  const availableScopes: TokenScope[] = [
    {
      name: "balance:read",
      description: "Read wallet balances",
      isDefault: true,
    },
    { name: "balance:write", description: "Update wallet balances" },
    {
      name: "transactions:read",
      description: "Read transaction history",
      isDefault: true,
    },
    { name: "transactions:create", description: "Create new transactions" },
    { name: "tokens:buy", description: "Buy tokens" },
    { name: "tokens:sell", description: "Sell tokens" },
    { name: "cryptocows:read", description: "Read CryptoCows data" },
    { name: "cryptocows:write", description: "Write CryptoCows data" },
    { name: "aurion:read", description: "Read AurionTrading data" },
    { name: "aurion:write", description: "Write AurionTrading data" },
    {
      name: "full:access",
      description: "Full access to all features (use sparingly)",
    },
  ];

  useEffect(() => {
    loadTokens();
  }, []);

  useEffect(() => {
    if (createdToken) {
      setSnackbar({
        open: true,
        message: `Token "${createdToken.tokenDetails?.name}" created successfully!`,
        severity: "success",
      });
      setCreateDialogOpen(false);
      loadTokens();
      // Reset form
      setFormData({
        name: "",
        scopes: [],
        expirationDays: 30,
        description: "",
      });
      setSelectedScopes([]);
    }
  }, [createdToken]);

  const loadTokens = async () => {
    try {
      await dispatch(fetchTokens({ skip: 0, take: 50 })).unwrap();
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to load tokens",
        severity: "error",
      });
    }
  };

  const handleCreateToken = async () => {
    try {
      if (!formData.name.trim()) {
        setSnackbar({
          open: true,
          message: "Token name is required",
          severity: "error",
        });
        return;
      }

      if (selectedScopes.length === 0) {
        setSnackbar({
          open: true,
          message: "At least one scope is required",
          severity: "error",
        });
        return;
      }

      await dispatch(
        createToken({
          name: formData.name,
          scopes: selectedScopes,
          expirationDays: formData.expirationDays,
        }),
      ).unwrap();

      // Show token in dialog
      setCreateDialogOpen(false);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to create token",
        severity: "error",
      });
    }
  };

  const handleRevokeToken = async (id: number) => {
    try {
      await dispatch(revokeToken(id)).unwrap();
      setSnackbar({
        open: true,
        message: "Token revoked successfully",
        severity: "success",
      });
      loadTokens();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to revoke token",
        severity: "error",
      });
    }
  };

  const handleDeleteToken = async () => {
    if (!selectedTokenId) return;

    try {
      await dispatch(deleteToken(selectedTokenId)).unwrap();
      setSnackbar({
        open: true,
        message: "Token deleted successfully",
        severity: "success",
      });
      setDeleteDialogOpen(false);
      setSelectedTokenId(null);
      loadTokens();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to delete token",
        severity: "error",
      });
    }
  };

  const handleCopyToken = (token: string) => {
    navigator.clipboard.writeText(token);
    setSnackbar({
      open: true,
      message: "Token copied to clipboard",
      severity: "info",
    });
  };

  const handleToggleShowToken = (tokenId: number, token: string) => {
    setShowToken(showToken === token ? null : token);
  };

  const handleScopeToggle = (scopeName: string) => {
    setSelectedScopes((prev) =>
      prev.includes(scopeName)
        ? prev.filter((s) => s !== scopeName)
        : [...prev, scopeName],
    );
  };

  const handleSelectAllScopes = () => {
    const defaultScopes = availableScopes
      .filter((s) => s.isDefault)
      .map((s) => s.name);
    setSelectedScopes(defaultScopes);
  };

  const handleClearScopes = () => {
    setSelectedScopes([]);
  };

  const getStatusColor = (
    token: PersonalAccessToken,
  ): "success" | "error" | "warning" | "default" => {
    if (!token.isActive) return "error";
    if (token.isExpired) return "warning";
    return "success";
  };

  const getStatusLabel = (token: PersonalAccessToken): string => {
    if (!token.isActive) return "Revoked";
    if (token.isExpired) return "Expired";
    return "Active";
  };

  const formatDate = (date: string | null) => {
    if (!date) return "Never";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5" fontWeight="bold">
          Personal Access Tokens
        </Typography>
        <Box>
          <Tooltip title="Refresh">
            <IconButton onClick={loadTokens} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{ ml: 1 }}
          >
            Create Token
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Tokens
              </Typography>
              <Typography variant="h5">{tokens.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active
              </Typography>
              <Typography variant="h5" color="success.main">
                {tokens.filter((t) => t.isActive && !t.isExpired).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Expired
              </Typography>
              <Typography variant="h5" color="warning.main">
                {tokens.filter((t) => t.isExpired).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Revoked
              </Typography>
              <Typography variant="h5" color="error.main">
                {tokens.filter((t) => !t.isActive).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tokens Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Token</TableCell>
              <TableCell>Scopes</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell>Last Used</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : tokens.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="textSecondary">
                    No tokens found. Create your first personal access token.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              tokens.map((token) => (
                <TableRow key={token.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {token.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Typography
                        variant="body2"
                        sx={{ mr: 1, fontFamily: "monospace" }}
                      >
                        {showToken === token.token
                          ? token.token
                          : token.tokenDisplay}
                      </Typography>
                      <Tooltip
                        title={
                          showToken === token.token
                            ? "Hide token"
                            : "Show token"
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleToggleShowToken(token.id, token.token)
                          }
                        >
                          {showToken === token.token ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy token">
                        <IconButton
                          size="small"
                          onClick={() => handleCopyToken(token.token)}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {token.scopes.slice(0, 3).map((scope: any) => (
                        <Chip
                          key={scope}
                          label={scope}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                      {token.scopes.length > 3 && (
                        <Chip
                          label={`+${token.scopes.length - 3}`}
                          size="small"
                          variant="outlined"
                          color="info"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(token)}
                      color={getStatusColor(token)}
                      size="small"
                    />
                    {token.daysUntilExpiry !== null &&
                      token.isActive &&
                      !token.isExpired && (
                        <Typography
                          variant="caption"
                          display="block"
                          color="textSecondary"
                        >
                          {token.daysUntilExpiry} days left
                        </Typography>
                      )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(token.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {token.expiresAtFormatted}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {token.lastUsedAt
                        ? formatDate(token.lastUsedAt)
                        : "Never"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {token.isActive && !token.isExpired && (
                      <Tooltip title="Revoke Token">
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => handleRevokeToken(token.id)}
                        >
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete Token">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setSelectedTokenId(token.id);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Token Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">Create Personal Access Token</Typography>
          <Typography variant="body2" color="textSecondary">
            Generate a new token for API access
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ pt: 1 }}>
            {/* Token Name */}
            <TextField
              fullWidth
              label="Token Name"
              placeholder="e.g., CryptoCows Integration"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              sx={{ mb: 3 }}
              required
              helperText="Give your token a descriptive name"
            />

            {/* Scopes Selection */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <FormLabel component="legend">Scopes</FormLabel>
              <Box display="flex" gap={1} mb={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSelectAllScopes}
                >
                  Select Default
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleClearScopes}
                >
                  Clear All
                </Button>
              </Box>
              <FormGroup>
                {availableScopes.map((scope) => (
                  <Box
                    key={scope.name}
                    sx={{ display: "flex", alignItems: "center", py: 0.5 }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedScopes.includes(scope.name)}
                          onChange={() => handleScopeToggle(scope.name)}
                          size="small"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">
                            {scope.name}
                            {scope.isDefault && (
                              <Chip
                                label="Default"
                                size="small"
                                color="info"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {scope.description}
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                ))}
              </FormGroup>
            </FormControl>

            {/* Expiration */}
            <TextField
              fullWidth
              label="Expiration (days)"
              type="number"
              value={formData.expirationDays || ""}
              onChange={(e) => {
                const value = e.target.value ? parseInt(e.target.value) : null;
                setFormData({ ...formData, expirationDays: value });
              }}
              InputProps={{
                inputProps: { min: 1, max: 365 },
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Leave empty for never expires">
                      <InfoIcon fontSize="small" color="action" />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              helperText="Number of days until token expires (1-365 days, leave empty for never)"
              sx={{ mb: 2 }}
            />

            {/* Security Warning */}
            <Paper
              variant="outlined"
              sx={{ p: 2, bgcolor: "warning.light", color: "warning.dark" }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <WarningIcon />
                <Typography variant="body2">
                  <strong>Important:</strong> The token will be shown only once
                  after creation. Make sure to copy and store it securely.
                </Typography>
              </Box>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateToken}
            disabled={!formData.name.trim() || selectedScopes.length === 0}
          >
            Generate Token
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Token</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this token? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteToken}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PersonalAccessTokenManagement;
